import { getStatusCopy } from '@/shared/copy/status';
import { formatPercent } from '@/ui/formatters/formatPercent';
import type { CoverageRunDto } from '@flowqa/shared';
import type { ReactNode } from 'react';

export type CoverageRunTrendDatum = {
  label: string;
  linesPct: number;
};

export type CoverageRunSummaryModel = {
  total: number;
  latestCapturedAt: Date | null;
  linesCovered: number;
  linesTotal: number;
  linesPct: number | null;
  functionsCovered: number | null;
  functionsTotal: number | null;
  functionsPct: number | null;
  branchesCovered: number | null;
  branchesTotal: number | null;
  branchesPct: number | null;
  statementsCovered: number | null;
  statementsTotal: number | null;
  statementsPct: number | null;
};

type CoverageMetricCardTone =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'brown'
  | 'primary'
  | 'default';
type CoverageMetricCardDeltaTone = 'positive' | 'negative' | 'neutral' | undefined;
type CoverageMetricCard = {
  id: string;
  status: ReactNode;
  statusTone: CoverageMetricCardTone;
  count: ReactNode;
  secondaryValue?: ReactNode;
  percentage: ReactNode;
  delta?: ReactNode;
  deltaTone?: CoverageMetricCardDeltaTone;
};

const formatCoveragePercent = (value: number | null | undefined): string =>
  value === null || value === undefined ? '-' : `${formatPercent(value)}%`;

const formatZeroPercent = (): string => formatCoveragePercent(0);

const formatCoveredTotal = (
  covered: number | null | undefined,
  total: number | null | undefined,
  label: string,
): string | undefined => {
  if (covered === null || covered === undefined || total === null || total === undefined) {
    return undefined;
  }
  if (total <= 0) {
    return undefined;
  }
  return `${covered} / ${total} ${label}`;
};

const formatCountDelta = (
  current: number | null | undefined,
  previous: number | null | undefined,
): string | undefined => {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return undefined;
  }
  const delta = current - previous;
  if (delta === 0) return '(0)';
  return `(${delta > 0 ? '+' : ''}${delta})`;
};

const formatDelta = (value: number | null, countDelta: string | undefined): string | undefined => {
  if (value === null) return undefined;
  if (value === 0) {
    const noChangeLabel = getStatusCopy('noChange');
    return countDelta ? `${noChangeLabel} ${countDelta}` : noChangeLabel;
  }
  const absoluteValue = Math.abs(Math.round(value * 100) / 100);
  const formattedPercent = `${value > 0 ? '+' : '-'}${absoluteValue.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  })}%`;
  return countDelta ? `${formattedPercent} ${countDelta}` : formattedPercent;
};

const resolveDeltaTone = (value: number | null): CoverageMetricCardDeltaTone => {
  if (value === null || value === 0) return 'neutral';
  return value > 0 ? 'positive' : 'negative';
};

const calculateAverageDelta = (
  current: number | null | undefined,
  previous: number | null | undefined,
): number | null => {
  if (current === null || current === undefined) {
    return null;
  }
  if (previous === null || previous === undefined) {
    return null;
  }
  return current - previous;
};

const hasMetricData = (
  covered: number | null | undefined,
  total: number | null | undefined,
): boolean =>
  covered !== null && covered !== undefined && total !== null && total !== undefined && total > 0;

const resolveMetricValue = (
  pct: number | null | undefined,
  covered: number | null | undefined,
  total: number | null | undefined,
): number | null => {
  if (pct === null || pct === undefined) {
    return null;
  }
  if (!hasMetricData(covered, total)) {
    return null;
  }
  return pct;
};

const resolveCoveredCountForDelta = (
  covered: number | null | undefined,
  total: number | null | undefined,
): number | undefined => (hasMetricData(covered, total) ? (covered ?? undefined) : undefined);

const sumNullable = (values: Array<number | null | undefined>): number | null => {
  const numericValues = values.filter(
    (value): value is number => value !== null && value !== undefined,
  );
  if (numericValues.length === 0) {
    return null;
  }
  return numericValues.reduce((sum, value) => sum + value, 0);
};

const toPct = (covered: number | null, total: number | null): number | null => {
  if (covered === null || total === null || total <= 0) {
    return null;
  }
  return (covered / total) * 100;
};

export const buildCoverageRunSummaryFromRuns = (
  runs: CoverageRunDto[],
): CoverageRunSummaryModel | null => {
  if (runs.length === 0) {
    return null;
  }

  const latestRun = runs[0];
  const linesCovered = runs.reduce((sum, run) => sum + run.linesCovered, 0);
  const linesTotal = runs.reduce((sum, run) => sum + run.linesTotal, 0);
  const functionsCovered = sumNullable(runs.map((run) => run.functionsCovered));
  const functionsTotal = sumNullable(runs.map((run) => run.functionsTotal));
  const branchesCovered = sumNullable(runs.map((run) => run.branchesCovered));
  const branchesTotal = sumNullable(runs.map((run) => run.branchesTotal));
  const statementsCovered = sumNullable(runs.map((run) => run.statementsCovered));
  const statementsTotal = sumNullable(runs.map((run) => run.statementsTotal));

  return {
    total: runs.length,
    latestCapturedAt: latestRun.capturedAt ?? latestRun.createdAt,
    linesCovered,
    linesTotal,
    linesPct: linesTotal > 0 ? (linesCovered / linesTotal) * 100 : null,
    functionsCovered,
    functionsTotal,
    functionsPct: toPct(functionsCovered, functionsTotal),
    branchesCovered,
    branchesTotal,
    branchesPct: toPct(branchesCovered, branchesTotal),
    statementsCovered,
    statementsTotal,
    statementsPct: toPct(statementsCovered, statementsTotal),
  };
};

export const buildCoverageRunTrendData = (runs: CoverageRunDto[]): CoverageRunTrendDatum[] =>
  [...runs].reverse().map((run, index) => ({
    label: run.commitSha?.slice(0, 8) ?? `Run ${index + 1}`,
    linesPct: run.linesPct,
  }));

const buildMetricCard = (
  id: string,
  label: string,
  value: string,
  supporting: string,
  tone: CoverageMetricCardTone,
  secondaryValue?: ReactNode,
  delta?: string,
  deltaTone?: CoverageMetricCardDeltaTone,
): CoverageMetricCard => ({
  id,
  status: label,
  statusTone: tone,
  count: value,
  percentage: supporting,
  secondaryValue,
  delta,
  deltaTone,
});

const buildEmptyMetricCard = (
  id: string,
  label: string,
  tone: CoverageMetricCardTone,
  secondaryLabel: string,
): CoverageMetricCard =>
  buildMetricCard(id, label, formatZeroPercent(), '', tone, `0 / 0 ${secondaryLabel}`);

export const buildCoverageRunMetricCards = (
  summary: CoverageRunSummaryModel | null,
  previousSummary?: CoverageRunSummaryModel | null,
): CoverageMetricCard[] => {
  if (summary === null) {
    return [
      buildEmptyMetricCard('lines', 'Line Coverage', 'primary', 'lines'),
      buildEmptyMetricCard('functions', 'Function Coverage', 'info', 'functions'),
      buildEmptyMetricCard('branches', 'Branch Coverage', 'warning', 'branches'),
      buildEmptyMetricCard('statements', 'Statement Coverage', 'success', 'statements'),
    ];
  }

  const linesPct = resolveMetricValue(summary.linesPct, summary.linesCovered, summary.linesTotal);
  const previousLinesPct = resolveMetricValue(
    previousSummary?.linesPct,
    previousSummary?.linesCovered,
    previousSummary?.linesTotal,
  );
  const functionsPct = resolveMetricValue(
    summary.functionsPct,
    summary.functionsCovered,
    summary.functionsTotal,
  );
  const previousFunctionsPct = resolveMetricValue(
    previousSummary?.functionsPct,
    previousSummary?.functionsCovered,
    previousSummary?.functionsTotal,
  );
  const branchesPct = resolveMetricValue(
    summary.branchesPct,
    summary.branchesCovered,
    summary.branchesTotal,
  );
  const previousBranchesPct = resolveMetricValue(
    previousSummary?.branchesPct,
    previousSummary?.branchesCovered,
    previousSummary?.branchesTotal,
  );
  const statementsPct = resolveMetricValue(
    summary.statementsPct,
    summary.statementsCovered,
    summary.statementsTotal,
  );
  const previousStatementsPct = resolveMetricValue(
    previousSummary?.statementsPct,
    previousSummary?.statementsCovered,
    previousSummary?.statementsTotal,
  );

  return [
    buildMetricCard(
      'lines',
      'Line Coverage',
      formatCoveragePercent(linesPct),
      '',
      'primary',
      formatCoveredTotal(summary.linesCovered, summary.linesTotal, 'lines'),
      formatDelta(
        calculateAverageDelta(linesPct, previousLinesPct),
        formatCountDelta(
          resolveCoveredCountForDelta(summary.linesCovered, summary.linesTotal),
          resolveCoveredCountForDelta(previousSummary?.linesCovered, previousSummary?.linesTotal),
        ),
      ),
      resolveDeltaTone(calculateAverageDelta(linesPct, previousLinesPct)),
    ),
    buildMetricCard(
      'functions',
      'Function Coverage',
      formatCoveragePercent(functionsPct),
      '',
      'info',
      formatCoveredTotal(summary.functionsCovered, summary.functionsTotal, 'functions'),
      formatDelta(
        calculateAverageDelta(functionsPct, previousFunctionsPct),
        formatCountDelta(
          resolveCoveredCountForDelta(summary.functionsCovered, summary.functionsTotal),
          resolveCoveredCountForDelta(
            previousSummary?.functionsCovered,
            previousSummary?.functionsTotal,
          ),
        ),
      ),
      resolveDeltaTone(calculateAverageDelta(functionsPct, previousFunctionsPct)),
    ),
    buildMetricCard(
      'branches',
      'Branch Coverage',
      formatCoveragePercent(branchesPct),
      '',
      'warning',
      formatCoveredTotal(summary.branchesCovered, summary.branchesTotal, 'branches'),
      formatDelta(
        calculateAverageDelta(branchesPct, previousBranchesPct),
        formatCountDelta(
          resolveCoveredCountForDelta(summary.branchesCovered, summary.branchesTotal),
          resolveCoveredCountForDelta(
            previousSummary?.branchesCovered,
            previousSummary?.branchesTotal,
          ),
        ),
      ),
      resolveDeltaTone(calculateAverageDelta(branchesPct, previousBranchesPct)),
    ),
    buildMetricCard(
      'statements',
      'Statement Coverage',
      formatCoveragePercent(statementsPct),
      '',
      'success',
      formatCoveredTotal(summary.statementsCovered, summary.statementsTotal, 'statements'),
      formatDelta(
        calculateAverageDelta(statementsPct, previousStatementsPct),
        formatCountDelta(
          resolveCoveredCountForDelta(summary.statementsCovered, summary.statementsTotal),
          resolveCoveredCountForDelta(
            previousSummary?.statementsCovered,
            previousSummary?.statementsTotal,
          ),
        ),
      ),
      resolveDeltaTone(calculateAverageDelta(statementsPct, previousStatementsPct)),
    ),
  ];
};
